import { useSyncExternalStore } from 'react'

/**
 * Module-scoped external store for Crisp's unread message count. `crisp-chat.tsx`
 * and `support-card.tsx` are siblings in the tree, not parent/child, so this is
 * the plumbing between them — mirrors the `useSyncExternalStore` pattern already
 * used by `use-scroll-reveal.ts` and `analytics-consent-provider.tsx`.
 *
 * crisp-sdk-web's own `Crisp.chat.unreadCount()` reads `window.$crisp.get(...)`,
 * which — like `isChatOpened()` — is unsafe to call before Crisp's script has
 * fully loaded. There is also no `onUnreadCountChanged` event in this SDK
 * version. So the count here is computed independently: incremented on every
 * `Crisp.message.onMessageReceived()` while the chat window is closed, reset to
 * 0 on `Crisp.chat.onChatOpened()` — both are queued `push()` calls and safe
 * pre-load.
 */
let unreadCount = 0
const listeners = new Set<() => void>()

function notify(): void {
  listeners.forEach((listener) => listener())
}

/** Called only from `crisp-chat.tsx`'s Crisp event handlers. */
export function incrementCrispUnreadCount(): void {
  unreadCount += 1
  notify()
}

/** Called only from `crisp-chat.tsx`'s Crisp event handlers. */
export function resetCrispUnreadCount(): void {
  if (unreadCount === 0) return
  unreadCount = 0
  notify()
}

/** Exported for direct (non-React) testing of the store; `useCrispUnreadCount` is the real call site. */
export function subscribeCrispUnreadCount(onChange: () => void): () => void {
  listeners.add(onChange)
  return () => listeners.delete(onChange)
}

/** Exported for direct (non-React) testing of the store; `useCrispUnreadCount` is the real call site. */
export function getCrispUnreadCountSnapshot(): number {
  return unreadCount
}

/** Live count of Crisp messages received while the chat window was closed. */
export function useCrispUnreadCount(): number {
  return useSyncExternalStore(subscribeCrispUnreadCount, getCrispUnreadCountSnapshot, () => 0)
}
